import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageSEO } from '../components/PageSEO';
import { 
  BookOpen, GraduationCap, Award, Sprout, TreePine, Flower2,
  ChevronDown, ChevronUp, ChevronRight, Calendar, MapPin, Clock,
  Users, Zap, Heart, Shield, Sun, CloudRain, Snowflake, Leaf,
  Bug, Droplets, Target, Star, ArrowRight, CheckCircle2
} from 'lucide-react';

// Hero stats
const heroStats = [
  { value: '150+', label: 'Mezun', icon: GraduationCap },
  { value: '12', label: 'Kurs', icon: BookOpen },
  { value: '35+', label: 'Saat İçerik', icon: Clock },
  { value: '25+', label: 'Uzman Eğitmen', icon: Award }
];

// Learning Paths
const learningPaths = [
  {
    level: 'Başlangıç',
    title: 'Arıcılığa İlk Adım',
    color: '#4a8c5e',
    icon: Sprout,
    duration: '8 saat',
    modüles: 4,
    description: 'Arıcılık dünyasına hoş geldiniz! Sıfırdan başlayarak temel bilgileri öğrenin.',
    topics: [
      'Arı biyolojisi ve koloni yapısı',
      'Temel ekipmanlar ve kovan tipleri',
      'İlk kovanınızı kurma rehberi',
      'Güvenlik ve koruyucu ekipman'
    ]
  },
  {
    level: 'Orta',
    title: 'Profesyonel Arıcılık',
    color: '#D4AF37',
    icon: TreePine,
    duration: '14 saat',
    modüles: 5,
    description: 'Arıcılığınızı profesyonel seviyeye taşıyın. Verimlilik ve kalite odaklı eğitim.',
    topics: [
      'Mevsimsel kovan yönetimi',
      'Hastalık ve zararlı kontrolü',
      'Bal hasadı ve işleme teknikleri',
      'Kraliçe yetiştirme temelleri',
      'Üretim planlaması ve kayıt tutma'
    ]
  },
  {
    level: 'İleri',
    title: 'Sürdürülebilir Üretim',
    color: '#1A3625',
    icon: Award,
    duration: '12 saat',
    modüles: 4,
    description: 'Doğayla uyumlu, sürdürülebilir ve ekonomik arıcılık uygulamaları.',
    topics: [
      'Organik arıcılık sertifikası',
      'Arı ürünleri çeşitlendirme (propolis, polen, arı sütü)',
      'Pazarlama ve marka oluşturma',
      'Ekosistem hizmetleri ve tozlaşma yönetimi'
    ]
  }
];

// Bee Encyclopedia
const beeEncyclopedia = [
  {
    title: 'Kraliçe Arı',
    emoji: null,
    icon: Star,
    color: '#D4AF37',
    lifespan: '3-5 yıl',
    role: 'Koloninin annesi',
    fact: 'Günde 2.000\'e kadar yumurta bırakır. Çiftleşme uçuşunda 17 km yüksekliğe çıkar.',
    details: 'Kolonideki tek üreme yeteneğine sahip dişi arıdır. Özel bir madde (kraliçe feromu) salgılayarak koloniyi bir arada tutar.'
  },
  {
    title: 'İşçi Arı',
    emoji: null,
    icon: Zap,
    color: '#4a8c5e',
    lifespan: '6 hafta (yaz)',
    role: 'Tüm işleri yapar',
    fact: 'Hayatı boyunca sadece 1/12 çay kaşığı bal üretir. Ama milyonlarcası birlikte mucizeler yaratır.',
    details: '21 günlük yaşam döngüsü içinde sırayla temizlikçi, bakıcı, mühendis, muhafız ve son olarak tarlacı olur.'
  },
  {
    title: 'Erkek Arı (Drone)',
    emoji: null,
    icon: Heart,
    color: '#8B5E3C',
    lifespan: '90 gün',
    role: 'Çiftleşme',
    fact: 'İğnesi yoktur, sokamaz. Kış gelince koloniden çıkarılır.',
    details: 'Tek görevi kraliçe arıyla çiftleşmektir. Gözleri işçi arılardan 3 kat büyüktür — çiftleşme uçuşunda kraliçeyi bulmak için.'
  },
  {
    title: 'Koloni Yapısı',
    emoji: null,
    icon: Users,
    color: '#1A3625',
    lifespan: 'Sürekli',
    role: '60.000 arı',
    fact: 'Bir kovandaki hijyen seviyesi ameliyathane ile yarışır.',
    details: 'Her koloni bir süperorganizmadır. Bireysel olarak basit kararlar alan arılar, bir arada inanılmaz karmaşık davranışlar sergiler.'
  }
];

// Seasonal Calendar - Enhanced
const seasonalCalendar = [
  { month: 'Ocak', season: 'winter', icon: Snowflake, color: '#94A3B8', temp: 6, 
    beeActivity: 5, nectarFlow: 0, beeState: 'Kış kümesi (küme halinde bekliyor)',
    image: '/images/calendar/ocak.jpg',
    tasks: [
      { text: 'Kovanları kesinlikle açmayın, yalnızca dışarıdan gözlem yapın', priority: 'critical' },
      { text: 'Kovan ağırlık takibi ile yem stokunu kontrol edin', priority: 'important' },
      { text: 'Kovan girişlerini temizleyin, ölü arıları uzaklaştırın', priority: 'important' },
      { text: 'Havalandırma deliklerini kontrol edin (nem birikimi önleme)', priority: 'normal' }
    ],
    tip: 'Arılar küme halinde kışlıyor, iç sıcaklığı 20-35°C arasında tutuyorlar. Kovanı açmak kümeyi bozar ve koloni kaybına yol açabilir.' },
  { month: 'Şubat', season: 'winter', icon: Snowflake, color: '#94A3B8', temp: 7,
    beeActivity: 10, nectarFlow: 0, beeState: 'Temizlik uçuşları başlar',
    image: '/images/calendar/subat.jpg',
    tasks: [
      { text: 'Yem stoku kontrolü yapın, gerekirse kek/fondant verin', priority: 'critical' },
      { text: 'Ilık günlerde kovan önündeki ölü arıları temizleyin', priority: 'important' },
      { text: 'Ekipman bakım ve onarımını tamamlayın', priority: 'normal' },
      { text: 'Yeni sezon için balmumu çerçeve hazırlığı', priority: 'normal' }
    ],
    tip: 'Arılar 10°C üzerinde temizlik uçuşuna çıkar. Kek takviyesi yaparken kovanı uzun süre açık bırakmayın. Şubat sonu-Mart başı uyandırma hazırlığı planlayın.' },
  { month: 'Mart', season: 'spring', icon: Flower2, color: '#4a8c5e', temp: 9,
    beeActivity: 30, nectarFlow: 10, beeState: 'Uyandırma ve ilk uçuşlar',
    image: '/images/calendar/mart.jpg',
    tasks: [
      { text: 'Mart ortası-sonunda ilk detaylı kovan kontrolü yapın', priority: 'critical' },
      { text: 'Ana arı varlığını ve yumurtlama düzenini kontrol edin', priority: 'critical' },
      { text: 'Teşvik besleme başlatın (1:1 şurup)', priority: 'important' },
      { text: 'İlk polen girişini gözlemleyin (badem, söğüt çiçekleri)', priority: 'normal' }
    ],
    tip: 'Marmara\'da uyandırma genelde Mart ortası yapılır. Ana arının düzenli yumurtladığını doğrulayın. Ana arısız kolonilere birleştirme uygulayın.' },
  { month: 'Nisan', season: 'spring', icon: Flower2, color: '#4a8c5e', temp: 13,
    beeActivity: 60, nectarFlow: 40, beeState: 'Hızlı koloni gelişimi',
    image: '/images/calendar/nisan.jpg',
    tasks: [
      { text: 'Haftalık oğul hücresi kontrolü yapın', priority: 'critical' },
      { text: 'Kovan genişletme, boş çerçeve verin', priority: 'important' },
      { text: 'Zayıf kolonilere destek (çerçeve veya birleştirme)', priority: 'important' },
      { text: 'Hastalık ve zararlı kontrolü (Varroa, nosema)', priority: 'normal' }
    ],
    tip: 'Meyve ağaçları ve ilkbahar florası çiçek açıyor. Koloni hızla büyür, yer sıkışıklığı oğul vermeye yol açar. Haftalık kontrol kritiktir.' },
  { month: 'Mayıs', season: 'spring', icon: Sun, color: '#D4AF37', temp: 18,
    beeActivity: 85, nectarFlow: 80, beeState: 'Ana nektar akımı başlangıcı',
    image: '/images/calendar/mayis.jpg',
    tasks: [
      { text: 'Oğul kontrolü ve önleme tedbirleri alın', priority: 'critical' },
      { text: 'Bal katı (süper) ekleyin, yer açın', priority: 'critical' },
      { text: 'Ana arı yetiştirme çalışmaları başlatın', priority: 'important' },
      { text: 'Akasya çiçeklenme dönemini takip edin', priority: 'normal' }
    ],
    tip: 'Marmara\'da ana bal akımı başlar. Akasya ve ilkbahar florası en güçlü dönemde. Oğul sezonu! Kovanları yakından takip edin, oğul hücreleri 7-8 günde bir kontrol edilmeli.' },
  { month: 'Haziran', season: 'summer', icon: Sun, color: '#D4AF37', temp: 22,
    beeActivity: 95, nectarFlow: 95, beeState: 'Doruk nektar akımı (ıhlamur/kestane)',
    image: '/images/calendar/haziran.jpg',
    tasks: [
      { text: 'Bal hasadı (çiçek balı), peteklerin %80\'i kapalıysa sağım yapın', priority: 'critical' },
      { text: 'Ihlamur ve kestane çiçeklenme dönemini takip edin', priority: 'important' },
      { text: 'Su kaynağı sağlayın, gölgeleme düzenlemesi yapın', priority: 'important' },
      { text: 'Oğul eğilimi kontrolüne devam edin', priority: 'normal' }
    ],
    tip: 'Yılın en verimli dönemi. Marmara\'da ıhlamur ve kestane çiçeklenmesi başlar. Hasat için peteklerin en az %80\'inin kapalı (mumlanmış) olmasını bekleyin.' },
  { month: 'Temmuz', season: 'summer', icon: Sun, color: '#F59E0B', temp: 24,
    beeActivity: 90, nectarFlow: 60, beeState: 'Hasat dönemi, nektar akımı azalır',
    image: '/images/calendar/temmuz.jpg',
    tasks: [
      { text: 'Ana bal hasadı (kestane balı sağımı)', priority: 'critical' },
      { text: 'Varroa düşüş sayımı yapın', priority: 'critical' },
      { text: 'Yeterli su kaynağı sağlamaya devam edin', priority: 'important' },
      { text: 'Sıcaklık stresi için havalandırma kontrolü', priority: 'normal' }
    ],
    tip: 'Kestane balı hasadı yapılır. Temmuz ortasından itibaren Marmara\'da nektar akımı belirgin şekilde azalır. Arılara kış için yeterli bal bırakmayı planlayın.' },
  { month: 'Ağustos', season: 'summer', icon: Sun, color: '#F59E0B', temp: 24,
    beeActivity: 65, nectarFlow: 20, beeState: 'Hasat sonu, kış arısı hazırlığı',
    image: '/images/calendar/agustos.jpg',
    tasks: [
      { text: 'Varroa tedavisi uygulayın (hasat sonrası zorunlu)', priority: 'critical' },
      { text: 'Son bal hasadını tamamlayın', priority: 'important' },
      { text: 'Kış arısı üretimi için besleme programı başlatın', priority: 'important' },
      { text: 'Ana arı performansını değerlendirin (değişim kararı)', priority: 'normal' }
    ],
    tip: 'Hasat sonrası Varroa tedavisi en kritik adımdır! Kış arısı sağlıklı doğmalıdır. Koloni başına en az 15-20 kg bal kış için bırakılmalıdır.' },
  { month: 'Eylül', season: 'autumn', icon: Leaf, color: '#C2410C', temp: 21,
    beeActivity: 40, nectarFlow: 10, beeState: 'Kış hazırlığı, besleme dönemi',
    image: '/images/calendar/eylul.jpg',
    tasks: [
      { text: 'Şurup ile takviye besleme yapın (2:1 şeker/su)', priority: 'critical' },
      { text: 'Zayıf kolonileri güçlülerle birleştirin', priority: 'critical' },
      { text: 'Kovan daraltma yapın (gereksiz çerçeveleri çıkarın)', priority: 'important' },
      { text: 'Son hastalık ve zararlı kontrolü', priority: 'normal' }
    ],
    tip: 'Kışa girecek koloni gücünü şimdi belirleyin. 5 çerçeve altı arılı zayıf kolonileri birleştirin. Eylül beslemesi kış stokunu oluşturur, geç kalmayın.' },
  { month: 'Ekim', season: 'autumn', icon: Leaf, color: '#C2410C', temp: 16,
    beeActivity: 20, nectarFlow: 2, beeState: 'Kışlama öncesi son hazırlıklar',
    image: '/images/calendar/ekim.jpg',
    tasks: [
      { text: 'Son Varroa tedavisini uygulayın (oksalik asit)', priority: 'critical' },
      { text: 'Kovan girişlerini daraltın (fare koruma teli takın)', priority: 'important' },
      { text: 'Kovan izolasyonu ve rüzgar koruması düzenleyin', priority: 'important' },
      { text: 'Yem stoğunu son kez kontrol edin', priority: 'normal' }
    ],
    tip: 'Kış hazırlığı Ekim sonuna kadar tamamlanmalı. Fare koruması çok önemli! Giriş daraltıcı takarak farelerin kovana girmesini önleyin.' },
  { month: 'Kasım', season: 'winter', icon: CloudRain, color: '#64748B', temp: 12,
    beeActivity: 10, nectarFlow: 0, beeState: 'Kümelenme başlangıcı',
    image: '/images/calendar/subat.jpg',
    tasks: [
      { text: 'Kovanları artık açmayın, yalnızca dışarıdan gözlem yapın', priority: 'critical' },
      { text: 'Kovanları rüzgara karşı sabitleyin', priority: 'important' },
      { text: 'Gerekirse fondan/kek desteği bırakın', priority: 'important' },
      { text: 'Nem ve havalandırma durumunu dış gözlemle değerlendirin', priority: 'normal' }
    ],
    tip: 'Arılar kümelenmeye başlar. Kovanı açmak koloniye zarar verir. Sadece kovan ağırlığı ve giriş aktivitesini dışarıdan kontrol edin.' },
  { month: 'Aralık', season: 'winter', icon: Snowflake, color: '#94A3B8', temp: 9,
    beeActivity: 5, nectarFlow: 0, beeState: 'Kışlama dönemi',
    image: '/images/calendar/ocak.jpg',
    tasks: [
      { text: 'Yalnızca dış gözlem yapın, kovanlara dokunmayın', priority: 'critical' },
      { text: 'Arıcılık eğitimlerine ve seminerlere katılın', priority: 'normal' },
      { text: 'Gelecek sezon için ekipman ve malzeme planlayın', priority: 'normal' },
      { text: 'Arıcılık kayıtlarınızı değerlendirip sezon planı yapın', priority: 'normal' }
    ],
    tip: 'Kışlama sürer. Kovanlara müdahale etmeyin. Kendinizi geliştirme zamanı: arıcılık kitapları, online eğitimler ve gelecek sezon planlaması için ideal dönem.' }
];

// Fun Facts
const funFacts = [
  { stat: '100.000+ km', description: '1 kg bal üretmek için arıların toplam uçuş mesafesi', icon: Zap },
  { stat: '4-5 milyon', description: '1 kg bal için ziyaret edilen çiçek sayısı', icon: Flower2 },
  { stat: '%75', description: 'Tozlaşmaya bağlı dünya bitki türlerinin oranı', icon: Sprout },
  { stat: '200+ milyar $', description: 'Arıların küresel tozlaşma ekonomik değeri', icon: Target },
  { stat: '200 kez/sn', description: 'Bir bal arısının saniyede kanat çırpma sayısı', icon: Droplets },
  { stat: '24 km/saat', description: 'Bir bal arısının ortalama uçuş hızı', icon: Zap }
];

// Upcoming Events
const upcomingEvents = [
  {
    title: 'Arıcılığa Giriş Atölyesi',
    date: '15 Nisan 2026',
    time: '10:00 - 16:00',
    location: 'KOSAD Eğitim Merkezi, Kocaeli',
    type: 'Atölye',
    level: 'Başlangıç',
    status: 'completed'
  },
  {
    title: 'Kraliçe Yetiştirme Semineri',
    date: '22 Nisan 2026',
    time: '13:00 - 17:00',
    location: 'Kandıra Arıcılık Çiftliği',
    type: 'Seminer',
    level: 'İleri',
    status: 'completed'
  },
  {
    title: 'Organik Bal Üretim Eğitimi',
    date: '3 Mayıs 2026',
    time: '09:00 - 15:00',
    location: 'KOSAD Eğitim Merkezi, Kocaeli',
    type: 'Eğitim',
    level: 'Orta',
    status: 'completed'
  },
  {
    title: 'Mevsimsel Kovan Yönetimi',
    date: '10 Mayıs 2026',
    time: '10:00 - 14:00',
    location: 'Online (Zoom)',
    type: 'Webinar',
    level: 'Başlangıç',
    status: 'completed'
  }
];

// FAQ
const faqs = [
  {
    question: 'Arıcılığa başlamak için ne kadar yatırım gerekiyor?',
    answer: 'Başlangıç için yaklaşık 5.000-10.000 TL yeterli olabilir. Bu, 2-3 kovan, koruyucu ekipman ve temel araçları kapsar. KOSAD üyelerine özel indirimli ekipman tedariki sağlıyoruz.'
  },
  {
    question: 'Şehirde arıcılık yapılabilir mi?',
    answer: 'Evet, kentsel arıcılık giderek yaygınlaşıyor. Çatılarda, bahçelerde arıcılık mümkün. Yerel yönetmelikleri kontrol etmeniz ve komşularınızı bilgilendirmeniz önemli.'
  },
  {
    question: 'Arı sokması tehlikeli mi?',
    answer: 'Çoğu insan için arı sokması sadece geçici bir ağrıya neden olur. Ancak alerjisi olanlar için ciddi olabilir. Koruyucu ekipman ve doğru tekniklerle sokma riski minimuma indirilir.'
  },
  {
    question: 'Ne zaman bal hasadı yapılır?',
    answer: 'Türkiye\'de genellikle Temmuz-Eylül arasında. Bölgeye ve çiçeklenme dönemine göre değişir. Peteklerin en az %80\'i kapalı olduğunda hasat edilebilir.'
  },
  {
    question: 'KOSAD eğitimlerine nasil katılabılırım?',
    answer: 'Web sitemizden veya iletişim formu aracılığıyla başvurabilirsiniz. Üyelerimize özel ücretsiz eğitimler düzenliyoruz. Üye olmayanlar da ücretli eğitimlere katılabilir.'
  },
  {
    question: 'Online eğitim seçenekleri var mi?',
    answer: 'Evet! Düzenli olarak Zoom üzerinden webinarlar düzenliyoruz. Ayrıca yakında kendi online eğitim platformumuzu da hayata geçirmeyi planlıyoruz.'
  }
];

const seasonColors = {
  winter: 'from-slate-100 to-blue-50',
  spring: 'from-green-50 to-emerald-50',
  summer: 'from-amber-50 to-yellow-50',
  autumn: 'from-orange-50 to-red-50'
};

export const EducationPage = () => {
  const [activePath, setActivePath] = useState(null);
  const [activeEncCard, setActiveEncCard] = useState(null);
  const [activeMonth, setActiveMonth] = useState(3); // April
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div data-testid="education-page">
      <PageSEO title="Arıcılık Akademisi" description="KOSAD Arıcılık Akademisi - Arıcılık eğitimleri, mevsimsel takvim, arı ansiklopedisi ve uzman tavsiyeleri." path="/egitim" />

      {/* Hero Section */}
      <section className="relative py-28 md:py-36 overflow-hidden" data-testid="edu-hero">
        <div className="absolute inset-0">
          <img 
            src="/images/harita-bg.jpg" 
            alt="Arıcılık Eğitimi" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3625]/95 via-[#1A3625]/80 to-[#1A3625]/40" />
        </div>
        
        <div className="container-custom relative">
          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
              <span className="label-uppercase text-[#D4AF37] mb-4 block">KOSAD Akademi</span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-semibold mb-6 leading-tight">
                Arıcılık<br />Akademisi
              </h1>
              <p className="text-lg text-white/80 mb-10 max-w-xl">
                Sıfırdan uzmana, arıcılık bilginizi geliştirin. 
                Deneyimli eğitmenler eşliğinde uygulamalı eğitim programları.
              </p>
            </motion.div>
            
            {/* Stats Row */}
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {heroStats.map((stat, i) => (
                <div key={i} className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/10" data-testid={`hero-stat-${i}`}>
                  <stat.icon className="w-5 h-5 text-[#D4AF37] mx-auto mb-2" />
                  <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-sm text-white/60 mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Learning Paths */}
      <section className="section-padding" data-testid="learning-paths">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Öğrenme Yolları</span>
            <h2 className="text-3xl md:text-4xl text-[#1A3625] mb-4">Seviyenize Uygun Eğitim</h2>
            <p className="text-base text-[#5C5C5C] max-w-2xl mx-auto">
              Her seviyeye özel hazırlanmış eğitim programlarıyla arıcılık yolculuğunuza başlayın veya geliştirin.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {learningPaths.map((path, index) => (
              <motion.div
                key={index}
                className="relative group cursor-pointer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                onClick={() => setActivePath(activePath === index ? null : index)}
                data-testid={`learning-path-${index}`}
              >
                <div className={`card-premium h-full transition-all duration-500 ${activePath === index ? 'ring-2 ring-[#D4AF37] shadow-xl' : ''}`}>
                  {/* Level Badge */}
                  <div 
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-base font-medium mb-6"
                    style={{ backgroundColor: path.color }}
                  >
                    <path.icon className="w-3.5 h-3.5" />
                    {path.level}
                  </div>

                  <h3 className="text-xl font-semibold text-[#1A3625] mb-3">{path.title}</h3>
                  <p className="text-base text-[#5C5C5C] mb-6">{path.description}</p>

                  {/* Meta */}
                  <div className="flex gap-4 mb-6 text-base text-[#8C7A6B]">
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {path.duration}</span>
                    <span className="flex items-center gap-1"><BookOpen className="w-4 h-4" /> {path.modüles} modül</span>
                  </div>

                  {/* Topics */}
                  <AnimatePresence>
                    {activePath === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="border-t border-[#E5DAC8] pt-5"
                      >
                        <p className="text-sm font-medium text-[#D4AF37] uppercase tracking-wider mb-3">Modüller</p>
                        <ul className="space-y-2">
                          {path.topics.map((topic, ti) => (
                            <li key={ti} className="flex items-start gap-2 text-base text-[#5C5C5C]">
                              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: path.color }} />
                              {topic}
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Expand indicator */}
                  <div className="mt-4 text-base font-medium flex items-center gap-1" style={{ color: path.color }}>
                    {activePath === index ? 'Daralt' : 'Detayları Gör'} 
                    <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activePath === index ? 'rotate-180' : ''}`} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bee Encyclopedia */}
      <section className="section-padding bg-[#1A3625] relative overflow-hidden" data-testid="bee-encyclopedia">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#2d5a3f]/30 rounded-full blur-3xl" />
        
        <div className="container-custom relative">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Arı Ansiklopedisi</span>
            <h2 className="text-3xl md:text-4xl text-white mb-4">Koloniyi Tanıyın</h2>
            <p className="text-base text-white/60 max-w-2xl mx-auto">
              Bir arı kolonisinin mucizevi yapısını ve her bireyin rolünü keşfet.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {beeEncyclopedia.map((bee, index) => (
              <motion.div
                key={index}
                className="group cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setActiveEncCard(activeEncCard === index ? null : index)}
                data-testid={`enc-card-${index}`}
              >
                <div className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 h-full transition-all duration-500 hover:bg-white/10 ${activeEncCard === index ? 'bg-white/10 ring-1 ring-[#D4AF37]/50' : ''}`}>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${bee.color}20` }}>
                    <bee.icon className="w-7 h-7" style={{ color: bee.color }} />
                  </div>

                  <h3 className="text-lg font-semibold text-white mb-2">{bee.title}</h3>
                  
                  <div className="flex gap-3 mb-4 text-base">
                    <span className="bg-white/10 px-2 py-1 rounded-lg text-white/70">{bee.lifespan}</span>
                    <span className="bg-white/10 px-2 py-1 rounded-lg text-white/70">{bee.role}</span>
                  </div>

                  <p className="text-white/60 text-base leading-relaxed mb-3">{bee.fact}</p>

                  <AnimatePresence>
                    {activeEncCard === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                      >
                        <div className="border-t border-white/10 pt-4 mt-2">
                          <p className="text-white/50 text-base leading-relaxed">{bee.details}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Seasonal Calendar */}
      <section className="section-padding" data-testid="seasonal-calendar">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Mevsimsel Rehber</span>
            <h2 className="text-3xl md:text-4xl text-[#1A3625] mb-4">Arıcılık Takvimi</h2>
            <p className="text-base text-[#5C5C5C] max-w-2xl mx-auto">
              Yılın her ayı için arıcılık rehberiniz. Doğru zamanda doğru müdahale.
            </p>
          </motion.div>

          {/* Month Selector */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {seasonalCalendar.map((item, index) => (
              <button
                key={index}
                onClick={() => setActiveMonth(index)}
                className={`px-4 py-2 rounded-full text-base font-medium transition-all duration-300 ${
                  activeMonth === index 
                    ? 'text-white shadow-lg scale-105' 
                    : 'bg-white text-[#5C5C5C] hover:bg-[#E5DAC8] border border-[#E5DAC8]'
                }`}
                style={activeMonth === index ? { backgroundColor: item.color } : {}}
                data-testid={`month-btn-${index}`}
              >
                {item.month}
              </button>
            ))}
          </div>

          {/* Active Month Detail */}
          <AnimatePresence mode="wait">
            {(() => {
              const cm = seasonalCalendar[activeMonth];
              const seasonLabel = cm.season === 'winter' ? 'Kış' : cm.season === 'spring' ? 'İlkbahar' : cm.season === 'summer' ? 'Yaz' : 'Sonbahar';
              const priorityStyles = {
                critical: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500', label: 'Kritik' },
                important: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Önemli' },
                normal: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Normal' }
              };
              return (
                <motion.div
                  key={activeMonth}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="max-w-5xl mx-auto rounded-3xl overflow-hidden shadow-xl border border-[#E5DAC8]/50"
                  data-testid="calendar-detail"
                >
                  {/* Top: Image + Stats Bar */}
                  <div className="relative h-56 md:h-64">
                    <img src={cm.image} alt={cm.month} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A3625]/90 via-[#1A3625]/40 to-transparent" />
                    
                    {/* Month Header Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                      <div className="flex items-end justify-between">
                        <div className="flex items-center gap-4">
                          {React.createElement(cm.icon, { className: "w-10 h-10 text-white/80" })}
                          <div>
                            <h3 className="text-3xl md:text-4xl font-bold text-white">{cm.month}</h3>
                            <p className="text-white/70 text-base">{seasonLabel} &middot; {cm.beeState}</p>
                          </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-1 bg-white/15 backdrop-blur-md rounded-2xl px-4 py-2 border border-white/20">
                          <Sun className="w-5 h-5 text-[#D4AF37]" />
                          <span className="text-2xl font-bold text-white">{cm.temp}°C</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content Area */}
                  <div className={`bg-gradient-to-br ${seasonColors[cm.season]} p-6 md:p-8`}>
                    
                    {/* Activity Bars */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                      <div className="sm:hidden flex items-center gap-2 bg-white/60 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/80">
                        <Sun className="w-5 h-5 text-[#D4AF37]" />
                        <span className="text-lg font-bold text-[#1A3625]">{cm.temp}°C</span>
                        <span className="text-sm text-[#5C5C5C]">Sıcaklık</span>
                      </div>
                      <div className="bg-white/60 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/80">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-base font-medium text-[#5C5C5C]">Arı Aktivitesi</span>
                          <span className="text-base font-bold text-[#1A3625]">%{cm.beeActivity}</span>
                        </div>
                        <div className="h-2.5 bg-[#1A3625]/10 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full rounded-full bg-[#4a8c5e]"
                            initial={{ width: 0 }}
                            animate={{ width: `${cm.beeActivity}%` }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                          />
                        </div>
                      </div>
                      <div className="bg-white/60 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/80">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-base font-medium text-[#5C5C5C]">Nektar Akışı</span>
                          <span className="text-base font-bold text-[#1A3625]">%{cm.nectarFlow}</span>
                        </div>
                        <div className="h-2.5 bg-[#1A3625]/10 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full rounded-full bg-[#D4AF37]"
                            initial={{ width: 0 }}
                            animate={{ width: `${cm.nectarFlow}%` }}
                            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.1 }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Tasks with Priority */}
                    <div className="mb-8">
                      <h4 className="text-base font-semibold text-[#D4AF37] uppercase tracking-wider mb-4">Yapılması Gerekenler</h4>
                      <div className="space-y-3">
                        {cm.tasks.map((task, i) => {
                          const ps = priorityStyles[task.priority] || priorityStyles.normal;
                          return (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.1 }}
                              className="flex items-center gap-3 bg-white/70 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/80"
                              data-testid={`task-${activeMonth}-${i}`}
                            >
                              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ps.dot}`} />
                              <span className="text-base text-[#1A3625] flex-1">{task.text}</span>
                              <span className={`text-sm font-semibold px-2.5 py-1 rounded-full ${ps.bg} ${ps.text}`}>
                                {ps.label}
                              </span>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Expert Tip */}
                    <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-white/80">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0">
                          <Shield className="w-5 h-5 text-[#D4AF37]" />
                        </div>
                        <div>
                          <p className="text-base font-semibold text-[#D4AF37] uppercase tracking-wider mb-1">Uzman Tavsiyesi</p>
                          <p className="text-base text-[#1A3625] leading-relaxed">{cm.tip}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>
      </section>

      {/* Fun Facts - Did You Know? */}
      <section className="section-padding bg-white" data-testid="fun-facts">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Şaşırtıcı Gerçekler</span>
            <h2 className="text-3xl md:text-4xl text-[#1A3625] mb-4">Biliyor muydunuz?</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {funFacts.map((fact, index) => (
              <motion.div
                key={index}
                className="card-premium text-center group"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`fun-fact-${index}`}
              >
                <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <fact.icon className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-[#1A3625] mb-2">{fact.stat}</div>
                <p className="text-base text-[#5C5C5C]">{fact.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="section-padding" data-testid="upcoming-events">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Etkinlikler</span>
            <h2 className="text-3xl md:text-4xl text-[#1A3625] mb-4">Tamamlanan Eğitimler</h2>
            <p className="text-base text-[#5C5C5C] max-w-2xl mx-auto">
              Başarıyla tamamlanan eğitim ve atölyelerimiz.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {upcomingEvents.map((event, index) => (
              <motion.div
                key={index}
                className="card-premium group hover:border-[#D4AF37]/30 transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`event-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex gap-2">
                    <span className="px-3 py-1.5 rounded-lg bg-[#1A3625] text-white text-base font-medium">{event.type}</span>
                    <span className="px-3 py-1.5 rounded-lg bg-[#D4AF37]/15 text-[#D4AF37] text-base font-medium">{event.level}</span>
                  </div>
                  <span className="text-base text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg font-medium">Tamamlandı ✓</span>
                </div>

                <h3 className="text-xl font-semibold text-[#1A3625] mb-4">{event.title}</h3>

                <div className="space-y-3 text-base text-[#5C5C5C] mb-6">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-[#D4AF37]" />
                    {event.date}
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#D4AF37]" />
                    {event.time}
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-[#D4AF37]" />
                    {event.location}
                  </div>
                </div>

                <span className="inline-flex items-center gap-2 text-emerald-600 font-semibold text-base">
                  Tamamlandı
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white" data-testid="faq-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="label-uppercase text-[#D4AF37] mb-4 block">SSS</span>
            <h2 className="text-3xl md:text-4xl text-[#1A3625]">
              Sıkça Sorulan Sorular
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className="card-premium"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between text-left"
                  data-testid={`faq-${index}`}
                >
                  <span className="text-base font-medium text-[#1A3625] pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  )}
                </button>
                
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 pt-4 border-t border-[#E5DAC8]"
                    >
                      <p className="text-[#5C5C5C]">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#1A3625] relative overflow-hidden" data-testid="edu-cta">
        <div className="absolute inset-0">
          <img 
            src="/images/logo-story-bg.jpg" 
            alt="" 
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="container-custom relative text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Hemen Başlayın</span>
            <h2 className="text-3xl md:text-4xl text-white mb-6">
              Arıcılık Yolculugunuza Başlayın
            </h2>
            <p className="text-lg text-white/70 mb-10">
              KOSAD Akademi eğitimlerine katılarak arıcılık bilginizi geliştirin, 
              topluluğumuzun bir parçası olun.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/katil"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1A3625] px-8 py-4 rounded-full font-semibold text-base hover:bg-[#c9a432] transition-colors"
                data-testid="cta-join-btn"
              >
                <GraduationCap className="w-5 h-5" />
                Eğitimlere Katıl
              </Link>
              <Link
                to="/iletisim"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-full font-semibold text-base hover:bg-white/10 transition-colors"
                data-testid="cta-contact-btn"
              >
                Bize Ulaşın
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
